import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type { MilestoneInput, TimelineSession } from '@/types/coach';
import { Button } from '@/components/ui/button';

export interface MilestoneEditInput {
  id: string;
  date: string;
  title: string;
  description: string | null;
  sessionId: string | null;
}

interface Props {
  clientId: string;
  sessions: TimelineSession[];
  initial?: MilestoneEditInput;
  defaultDate?: string;
  onClose: () => void;
  onSaved: () => void;
}

function toDateInput(iso: string): string {
  return iso.slice(0, 10);
}

export function MilestoneDialog({
  clientId,
  sessions,
  initial,
  defaultDate,
  onClose,
  onSaved,
}: Props) {
  const [date, setDate] = useState(
    initial
      ? toDateInput(initial.date)
      : toDateInput(defaultDate ?? new Date().toISOString()),
  );
  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [sessionId, setSessionId] = useState<string>(initial?.sessionId ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    if (!title.trim()) {
      setError('Titel ist erforderlich.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body: MilestoneInput = {
        date: new Date(date).toISOString(),
        title: title.trim(),
        description: description.trim() || null,
        sessionId: sessionId || null,
      };
      if (initial) {
        await api.coach.milestones.update(initial.id, body);
      } else {
        await api.coach.milestones.create(clientId, body);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-lg">
        <h2 className="mb-4 text-base font-semibold">
          {initial ? 'Meilenstein bearbeiten' : 'Neuer Meilenstein'}
        </h2>

        <div className="space-y-3">
          <label className="block text-sm">
            <span className="mb-1 block text-muted-foreground">Datum</span>
            <input
              type="date"
              className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-muted-foreground">Titel</span>
            <input
              type="text"
              className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="z. B. Erstes Erfolgserlebnis"
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-muted-foreground">
              Beschreibung (optional)
            </span>
            <textarea
              rows={3}
              className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-muted-foreground">
              Mit Session verknüpfen (optional)
            </span>
            <select
              className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
              value={sessionId}
              onChange={(e) => setSessionId(e.target.value)}
            >
              <option value="">Keine Verknüpfung</option>
              {sessions.map((s, idx) => (
                <option key={s.id} value={s.id}>
                  {idx === 0 ? 'Erstgespräch' : `Session ${idx + 1}`} —{' '}
                  {new Date(s.date).toLocaleDateString('de-DE')}
                </option>
              ))}
            </select>
          </label>
        </div>

        {error && <p className="mt-3 text-xs text-destructive">{error}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Speichern…' : 'Speichern'}
          </Button>
        </div>
      </div>
    </div>
  );
}
